import { useEffect, useState } from 'react'
import {
  formatMoneyDisplay,
  formatMoneyTyping,
  moneyInputToNumber,
  moneyInputToNumericString,
  numericStringToMoneyDisplay,
} from '../utils/moneyInput'

type MoneyInputProps = {
  value: string
  onChange: (next: string) => void
  allowNegative?: boolean
  allowEmpty?: boolean
  disabled?: boolean
  className?: string
  placeholder?: string
  'aria-label'?: string
}

export function MoneyInput({
  value,
  onChange,
  allowNegative = true,
  allowEmpty = true,
  disabled = false,
  className = '',
  placeholder = '$0.00',
  'aria-label': ariaLabel,
}: MoneyInputProps) {
  const opts = { allowNegative }
  const [focused, setFocused] = useState(false)
  const [text, setText] = useState(() => numericStringToMoneyDisplay(value, { allowEmpty }))

  useEffect(() => {
    if (focused) return
    setText(numericStringToMoneyDisplay(value, { allowEmpty }))
  }, [value, focused, allowEmpty])

  return (
    <input
      type="text"
      inputMode="decimal"
      autoComplete="off"
      spellCheck={false}
      className={`input input-bordered w-full ${className}`.trim()}
      value={text}
      placeholder={placeholder}
      aria-label={ariaLabel}
      disabled={disabled}
      onFocus={(e) => {
        setFocused(true)
        const stored = String(value ?? '').trim()
        if (stored && stored !== '-') {
          const n = Number(stored)
          if (Number.isFinite(n)) setText(formatMoneyTyping(String(n), opts))
        }
        requestAnimationFrame(() => e.target.select())
      }}
      onChange={(e) => {
        const next = formatMoneyTyping(e.target.value, opts)
        setText(next)
        onChange(moneyInputToNumericString(next, opts))
      }}
      onBlur={() => {
        setFocused(false)
        const n = moneyInputToNumber(text, opts)
        if (n == null) {
          if (allowEmpty) {
            setText('')
            onChange('')
            return
          }
          setText(formatMoneyDisplay(0))
          onChange('0')
          return
        }
        setText(formatMoneyDisplay(n))
        onChange(String(n))
      }}
    />
  )
}

export default MoneyInput
